import { useState, useEffect } from 'react'
import { useAuth } from 'contexts/AuthContext'
import { useEmailSignatureContext, useInitializeEmailSignature } from 'contexts/EmailSignatureContext'
import { getCardById } from 'apis/cardApi'
import { useTeamMember } from 'team/contexts/teamMemberContext'

/**
 * Custom hook to handle email signature card initialization
 * For team level: Uses team owner/admin member's default card
 * @param {Object} team - Team object (optional, for team-level email signature)
 * @returns {Object} { defaultCardId, loading, emailSignatureState }
 */
export const useEmailSignatureCard = (team = null) => {
    const { authState } = useAuth()
    const { getMembers, teamMembers } = useTeamMember()
    const [loading, setLoading] = useState(true)
    const [defaultCardId, setDefaultCardId] = useState(null)

    // Fetch team members if team is provided
    useEffect(() => {
        if (team && team._id) {
            getMembers(team._id)
        }
    }, [team, getMembers])

    // Get default card ID - team owner/admin member's card for team level, logged-in user's card otherwise
    useEffect(() => {
        if (team && team._id) {
            // Team level: Find team owner/admin member and use their default card
            const members = teamMembers[team._id]?.members || []
            const teamAdminMember = members.find(member =>
                member.role === 'admin' || member.membershipInfo?.role === 'admin'
            )

            if (teamAdminMember?.defaultCard?._id) {
                console.log('🔍 useEmailSignatureCard: Found team admin member:', teamAdminMember)
                console.log('🔍 useEmailSignatureCard: Using team admin default card:', teamAdminMember.defaultCard._id)
                setDefaultCardId(teamAdminMember.defaultCard._id)
            } else {
                console.warn('🔍 useEmailSignatureCard: No team admin member found or no default card')
                // Fallback to logged-in user's card
                if (authState?.user?.contactCardIds?.length > 0) {
                    const cardId = authState.user.contactCardIds.find(id =>
                        authState.cardState?.[id]?.is_default
                    ) || authState.user.contactCardIds[0]
                    setDefaultCardId(cardId)
                }
            }
        } else {
            // Individual/team member level: Use logged-in user's default card
            if (authState?.user?.contactCardIds?.length > 0) {
                const cardId = authState.user.contactCardIds.find(id =>
                    authState.cardState?.[id]?.is_default
                ) || authState.user.contactCardIds[0]
                setDefaultCardId(cardId)
            }
        }
    }, [authState, team, teamMembers])

    // Initialize email signature context with default card ID
    useInitializeEmailSignature(defaultCardId)

    const {
        emailSignatureState,
        setEmailSignatureName,
        setEmailSignatureCompany,
        setEmailSignatureJobTitle,
        setEmailSignaturePhoneNumber,
        setEmailSignatureLocation,
        setEmailSignatureProfilePictureUrl,
        setEmailSignatureCompanyLogoUrl,
    } = useEmailSignatureContext()

    // Fetch card details and store in state (similar to team member level pattern)
    const [cardDetails, setCardDetails] = useState(null)

    useEffect(() => {
        const fetchCardDetails = async () => {
            if (!defaultCardId) {
                setLoading(false)
                return
            }
            try {
                const data = await getCardById(defaultCardId)
                console.log('🔍 useEmailSignatureCard: Card data fetched:', data)
                setCardDetails(data)
            } catch (error) {
                console.error('Error fetching card details:', error)
                setLoading(false)
            }
        }

        if (defaultCardId) {
            fetchCardDetails()
        } else {
            setLoading(false)
        }
    }, [defaultCardId])

    // Set email signature state from card details (separate effect, like team member level)
    useEffect(() => {
        if (cardDetails?.card && defaultCardId) {
            const { fullName, jobTitle, company, location, phoneNumber } = cardDetails.card.contact_info || {}

            console.log('🔍 useEmailSignatureCard: Setting email signature state:', {
                cardId: defaultCardId,
                contact_info: cardDetails.card.contact_info,
                fullName,
                jobTitle,
                company,
                location,
                phoneNumber,
                profilePictureUrl: cardDetails.card.profilePictureUrl,
                companyLogoUrl: cardDetails.card.companyLogoUrl
            })

            // Update context state only if values have changed and are not undefined (same pattern as team member level)
            if (fullName && emailSignatureState[defaultCardId]?.emailSignatureName !== fullName) {
                console.log('🔍 useEmailSignatureCard: Setting name:', fullName)
                setEmailSignatureName(defaultCardId, fullName)
            }
            if (jobTitle && emailSignatureState[defaultCardId]?.emailSignatureJobTitle !== jobTitle) {
                console.log('🔍 useEmailSignatureCard: Setting jobTitle:', jobTitle)
                setEmailSignatureJobTitle(defaultCardId, jobTitle)
            }
            if (company && emailSignatureState[defaultCardId]?.emailSignatureCompany !== company) {
                console.log('🔍 useEmailSignatureCard: Setting company:', company)
                setEmailSignatureCompany(defaultCardId, company)
            }
            if (location && emailSignatureState[defaultCardId]?.emailSignatureLocation !== location) {
                console.log('🔍 useEmailSignatureCard: Setting location:', location)
                setEmailSignatureLocation(defaultCardId, location)
            }
            if (phoneNumber && emailSignatureState[defaultCardId]?.emailSignaturePhoneNumber !== phoneNumber) {
                console.log('🔍 useEmailSignatureCard: Setting phoneNumber:', phoneNumber)
                setEmailSignaturePhoneNumber(defaultCardId, phoneNumber)
            }

            // Set image URLs from card data
            if (cardDetails.card.profilePictureUrl && emailSignatureState[defaultCardId]?.emailSignatureProfilePictureUrl !== cardDetails.card.profilePictureUrl) {
                console.log('🔍 useEmailSignatureCard: Setting profilePictureUrl:', cardDetails.card.profilePictureUrl)
                setEmailSignatureProfilePictureUrl(defaultCardId, cardDetails.card.profilePictureUrl)
            }
            if (cardDetails.card.companyLogoUrl && emailSignatureState[defaultCardId]?.emailSignatureCompanyLogoUrl !== cardDetails.card.companyLogoUrl) {
                console.log('🔍 useEmailSignatureCard: Setting companyLogoUrl:', cardDetails.card.companyLogoUrl)
                setEmailSignatureCompanyLogoUrl(defaultCardId, cardDetails.card.companyLogoUrl)
            }

            setLoading(false)
        } else if (!defaultCardId) {
            setLoading(false)
        }
    }, [cardDetails, defaultCardId, emailSignatureState, setEmailSignatureName, setEmailSignatureJobTitle, setEmailSignatureCompany, setEmailSignatureLocation, setEmailSignaturePhoneNumber, setEmailSignatureProfilePictureUrl, setEmailSignatureCompanyLogoUrl])

    return { defaultCardId, loading, emailSignatureState, setLoading }
}

